import { IFilters } from '@types';
import { useEffect, useState } from 'react';

import { NextPage } from 'next';
import { NextSeo, NextSeoProps } from 'next-seo';
import { useRouter } from 'next/router';

import { useUpdateEffect } from '@hooks';
import { useQueryState } from '@hooks/useQueryState';

import { localeToSaleorQuery } from '@utils/regions';
import { convertToAttributeScalar } from '@utils/misc';
import { mapEdgesToItems } from '@utils/maps';
import { generateKey } from '@utils/core';

import { Loader } from '@components/atoms/Loader';

import {
  AttributeFragment,
  ProductCardFragment,
} from '@sdk/gqlTypes/generatedOperations';
import { useProductListLazyQuery } from '@sdk/gqlTypes/generatedHooks';

import { CategoryFragment } from '@prismic/gqlTypes/prismicOperations';

import {
  parseQueryAttributeFilters,
  serializeQueryAttributeFilters,
  filtersChangeHandler,
} from './utils';

import Page from './Page';

export interface ViewProps {
  params: { slug: string; id: string };
  collection: CategoryFragment | null;
  attributes: AttributeFragment[] | null;
  products: ProductCardFragment[] | null;
  seo: NextSeoProps;
}

const PRODUCTS_PER_PAGE = 100;

const View: NextPage<ViewProps> = ({
  collection,
  attributes,
  products,
  seo,
  params: { id: collectionId },
}) => {
  const { locale } = useRouter();

  const benefits = collection?.banners || [];

  const header = {
    name: collection?.name || '',
    excerpt: collection?.excerpt || '',
  };

  const [attributeFilters, setAttributeFilters] = useQueryState('filters', {
    parse: parseQueryAttributeFilters,
    serialize: serializeQueryAttributeFilters,
    defaultValue: {},
  });

  // State for the filteredProducts
  const [filteredProducts, setfilteredProducts] = useState([...products]);

  // State for the list
  const [list, setList] = useState([...products.slice(0, PRODUCTS_PER_PAGE)]);

  // State to trigger load more
  const [loadMore, setLoadMore] = useState(false);

  // State of whether there is more to load
  const [hasMore, setHasMore] = useState(products.length > PRODUCTS_PER_PAGE);

  // Load more button click
  const onLoadMore = () => {
    setLoadMore(true);
  };

  const filters: IFilters = {
    attributes: attributeFilters,
    pageSize: PRODUCTS_PER_PAGE,
    priceGte: null,
    priceLte: null,
  };

  const clearFilters = () => {
    setAttributeFilters({});
  };

  const onFiltersChange = filtersChangeHandler(
    filters,
    attributeFilters,
    setAttributeFilters
  );

  const [filterAttributes, { loading, data }] = useProductListLazyQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      filter: {
        attributes: filters.attributes
          ? convertToAttributeScalar(filters.attributes)
          : [],
        collections: collectionId
          ? [generateKey(collectionId, 'Collection')]
          : [],
        price: {
          gte: filters.priceGte,
          lte: filters.priceLte,
        },
      },
      first: 100,
      ...localeToSaleorQuery(locale),
      sortBy: null,
    },
  });

  // Handle loading more items
  useEffect(() => {
    if (loadMore && hasMore) {
      const currentLength = list.length;
      const isMore = currentLength < filteredProducts.length;
      const nextResults = isMore
        ? filteredProducts.slice(
            currentLength,
            currentLength + PRODUCTS_PER_PAGE
          )
        : [];
      setList([...list, ...nextResults]);
      setLoadMore(false);
    }
  }, [loadMore, hasMore]); //eslint-disable-line

  // Check if there is more
  useEffect(() => {
    const isMore = list.length < filteredProducts.length;
    setHasMore(isMore);
  }, [filteredProducts.length, list.length]); //eslint-disable-line

  // Reload onFiltersChange
  useUpdateEffect(() => {
    if (attributeFilters) {
      filterAttributes();
    } else {
      setfilteredProducts(products);
      setList([...products.slice(0, PRODUCTS_PER_PAGE)]);
    }
  }, [attributeFilters, products]); //eslint-disable-line

  // Set the filteredProducts List
  useEffect(() => {
    if (data) {
      const _products = mapEdgesToItems(data?.products);
      setfilteredProducts(_products);
      setList([..._products.slice(0, PRODUCTS_PER_PAGE)]);
    }
  }, [data]); //eslint-disable-line

  // Track productList Filtered
  useUpdateEffect(() => {
    // @ analytics
    let productsList = [] as Array<{
      category: string;
      name: string;
      product_id: string;
    }>;
    if (list) {
      productsList = list.map((z) => ({
        category: z.category.slug,
        name: z.productType.slug,
        product_id: z.slug,
      }));
    }
    let productsFilters = [] as Array<{ type: string; value: string }>;
    if (Object.keys(attributeFilters).length) {
      productsFilters = Object.entries(attributeFilters).map(([k, v]) => ({
        type: k,
        value: v.join('&'),
      }));
    }
    window?.rudderanalytics?.track('Product List Filtered', {
      list_id: 'category',
      filters: productsFilters,
      products: productsList,
    });
  }, [list, attributeFilters]);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <NextSeo {...seo} />
      <Page
        header={header}
        clearFilters={clearFilters}
        attributes={attributes}
        displayLoader={loading}
        hasNextPage={hasMore}
        filters={filters}
        products={list}
        benefits={benefits}
        onFiltersChange={onFiltersChange}
        onLoadMore={onLoadMore}
        activeFilters={
          filters!.attributes ? Object.keys(filters!.attributes).length : 0
        }
      />
    </>
  );
};

View.displayName = 'CollectionView';

export default View;
